import type { Service, ServiceList } from "@eai-toolkit/client";
import { ServiceApi } from "@eai-toolkit/client";
import { filtersToQ } from "@store/utils/queryUtils";
import { constructOrderParameter } from "@store/utils/sortUtils";

import { hydrateServicesQueryStateFromString } from "../queryState";
import { baseApi, handleQueryError } from "./baseApi";
import { axiosInstance, getSingleResource } from "./utils";

const serviceApi = new ServiceApi(undefined, undefined, axiosInstance);

export const servicesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getServiceById: builder.query<Service, { id: string }>({
      queryFn: async ({ id: serviceId }) => {
        try {
          const result = await getSingleResource(() =>
            serviceApi.v1ServiceGetById({ serviceId, human: true }),
          );
          if (result.error) {
            return {
              error: { status: "CUSTOM_ERROR", error: result.error },
            };
          }
          return { data: result.data };
        } catch (error) {
          return handleQueryError(error);
        }
      },
      providesTags: (_result, _error, { id }) => [{ type: "Service", id }],
    }),

    getServices: builder.query<ServiceList, { queryKey: string }>({
      queryFn: async ({ queryKey }) => {
        try {
          const queryState = hydrateServicesQueryStateFromString(queryKey);
          const {
            paging: { offset, limit },
            query: { filters, sort },
          } = queryState;

          const order = constructOrderParameter(sort.fieldName, sort.direction);

          const response = await serviceApi.v1ClusterServiceGet({
            offset,
            limit,
            q: filtersToQ(filters),
            order,
            human: true,
          });

          return { data: response.data };
        } catch (error) {
          return handleQueryError(error);
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...(result.items ?? []).map(({ id }) => ({
                type: "Service" as const,
                id,
              })),
              { type: "Service", id: "LIST" },
            ]
          : [{ type: "Service", id: "LIST" }],
    }),
  }),
});

export const { useGetServiceByIdQuery, useGetServicesQuery } = servicesApi;
